import { REPORT_COLORS } from "../../pages/CustomerReports/mf/mfReportsTheme";
import {
  isEquityBuyType,
  isEquitySellType,
} from "./equityTransactionTypes";

/** Column header for Unrealized / Realized holding rows (report tables). */
export const REPORT_GAIN_TYPE_COLUMN_LABEL = "Gain Type";

export const isReportUnrealizedTypeLabel = (label?: string | null): boolean => {
  const lower = String(label ?? "").trim().toLowerCase();
  return (
    lower === "unrealized" ||
    lower === "unrealised" ||
    lower === "gain type"
  );
};

export const isReportRealizedTypeLabel = (label?: string | null): boolean => {
  const lower = String(label ?? "").trim().toLowerCase();
  return lower === "realized" || lower === "realised";
};

const REPORT_INVESTED_AMOUNT_ROW_FIELDS = [
  "costValue",
  "investedAmount",
  "amount",
  "netPurchase",
  "purchaseValue",
  "purchaseAmount",
  "invested_amount",
  "amount_invested",
] as const;

/** BUY/SELL txn rows — SELL shows realised P&L; BUY shows unrealised P&L. */
export const resolveTxnIsSellForPnl = (
  row: Record<string, unknown> | null | undefined,
): boolean | null => {
  if (!row || typeof row !== "object") return null;
  const candidates = [
    row.transType,
    row.txnType,
    row.transactionType,
    row.transactiontype,
  ];
  for (const raw of candidates) {
    const label = String(raw ?? "").trim();
    if (!label) continue;
    if (isEquitySellType(label)) return true;
    if (isEquityBuyType(label)) return false;
  }
  const txnType = String(
    row.transType ??
      row.txnType ??
      row.transactionType ??
      row.transactiontype ??
      "",
  ).toLowerCase();
  if (!txnType) return null;
  if (
    txnType.includes("sell") ||
    txnType.includes("redemption") ||
    txnType.includes("redeem")
  ) {
    return true;
  }
  if (txnType.includes("buy") || txnType.includes("purchase")) {
    return false;
  }
  return null;
};

/** Row is a Realized gain-type row (Gain Type / type / displayTransType). */
export const isReportRealizedGainRow = (
  row: Record<string, unknown> | null | undefined,
): boolean => {
  if (!row || typeof row !== "object") return false;
  const labels = [
    row.type,
    row.displayTransType,
    row.displayTransactionType,
    row.gainType,
  ];
  if (
    labels.some((l) => isReportUnrealizedTypeLabel(String(l ?? "")))
  ) {
    return false;
  }
  return labels.some((l) => isReportRealizedTypeLabel(String(l ?? "")));
};

/** Row is an Unrealized gain-type row (Gain Type / type / displayTransType). */
export const isReportUnrealizedGainRow = (
  row: Record<string, unknown> | null | undefined,
): boolean => {
  if (!row || typeof row !== "object") return false;
  const labels = [
    row.type,
    row.displayTransType,
    row.displayTransactionType,
    row.gainType,
  ];
  if (labels.some((l) => isReportRealizedTypeLabel(String(l ?? "")))) {
    return false;
  }
  return labels.some((l) => isReportUnrealizedTypeLabel(String(l ?? "")));
};

/** Backend `amount` for Sale Value — read before invested fields are dashed. */
export const resolveReportSaleAmount = (
  row: Record<string, unknown> | null | undefined,
): number | null => {
  if (!row || typeof row !== "object") return null;
  const meta =
    row.metadata && typeof row.metadata === "object"
      ? (row.metadata as Record<string, unknown>)
      : row.meta && typeof row.meta === "object"
        ? (row.meta as Record<string, unknown>)
        : null;
  const sources = [row.saleValue, row.amount, meta?.amount];
  for (const raw of sources) {
    if (raw === null || raw === undefined || raw === "" || raw === "-") {
      continue;
    }
    const n =
      typeof raw === "number"
        ? raw
        : Number(String(raw).replace(/,/g, "").trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const applyInvestedAmountDash = <T extends Record<string, unknown>>(row: T): T => {
  const next: Record<string, unknown> = { ...row };
  const saleAmount = resolveReportSaleAmount(next);
  if (saleAmount !== null) {
    next.saleValue = saleAmount;
  }
  for (const key of REPORT_INVESTED_AMOUNT_ROW_FIELDS) {
    if (key in next) next[key] = "-";
  }
  return next as T;
};

/** Force invested-style fields to "-" (entire Realized report). */
export const forceBlankInvestedAmountFields = <
  T extends Record<string, unknown>,
>(
  row: T,
): T => applyInvestedAmountDash(row);

/** Show "-" for invested-style fields when Gain Type is Realized. */
export const blankInvestedAmountForRealizedGainRow = <
  T extends Record<string, unknown>,
>(
  row: T,
): T => {
  if (!isReportRealizedGainRow(row)) return row;
  return applyInvestedAmountDash(row);
};

/** Unrealized = primary blue; Realized = amber (report tables only). */
export const getReportHoldingTypeColor = (
  label?: string | null,
): string | undefined => {
  if (isReportUnrealizedTypeLabel(label)) return REPORT_COLORS.button;
  if (isReportRealizedTypeLabel(label)) return REPORT_COLORS.realized;
  return undefined;
};

/** Report `type` / `transType` cells — Unrealized label for display (styled via getReportHoldingTypeColor). */
export const formatReportTypeColumnLabel = (
  type?: string | null,
  emptyFallback = "-",
): string => {
  const raw = String(type ?? "").trim();
  if (!raw) return emptyFallback;
  const lower = raw.toLowerCase();
  if (lower === "unrealized" || lower === "unrealised" || lower === "gain type")
    return "Unrealized";
  if (lower === "realized" || lower === "realised") return "Realized";
  return raw;
};

/** @deprecated Same as formatReportTypeColumnLabel */
export const formatReportTransTypeColumnLabel = formatReportTypeColumnLabel;

export const REPORT_TRANS_TYPE_COLUMN_KEYS = new Set([
  "transType",
  "txnType",
  "transactionType",
  "transactiontype",
]);

export const isReportTransTypeColumnKey = (key: string): boolean =>
  REPORT_TRANS_TYPE_COLUMN_KEYS.has(String(key));

/** @deprecated Use formatReportTypeColumnLabel */
export const formatReportTxnTypeLabel = formatReportTypeColumnLabel;

const REPORT_TYPE_COLUMN_KEYS = new Set([
  "type",
  "transType",
  "txnType",
]);

export const isReportTypeColumnKey = (key: string): boolean =>
  REPORT_TYPE_COLUMN_KEYS.has(String(key));

/** If type/transType exists outside column 2, swap it into index 1 (all reports). */
export const ensureReportTypeColumnSecond = <T extends { key: string }>(
  cols: T[],
): T[] => {
  if (!Array.isArray(cols) || cols.length < 2) return cols;
  const typeIdx = cols.findIndex((c) => isReportTypeColumnKey(String(c.key)));
  if (typeIdx < 0) return cols;
  const targetTypeIdx = cols[1]?.key === "isin" ? 2 : 1;
  if (typeIdx === targetTypeIdx) return cols;
  const next = [...cols];
  const typeCol = next[typeIdx];
  const displaced = next[targetTypeIdx];
  next[targetTypeIdx] = typeCol;
  next[typeIdx] = displaced;
  return next;
};
